import * as Sentry from '@sentry/vue'
import {appUrls} from '@/constants/app.js'
import {useEnv} from '@/composables/use-env.js'
import {useTeams} from '@/composables/use-teams.js'
import {useCurrentUser} from '@/composables/use-current-user.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {watch} from 'vue'

/**
 * Sentry initializer
 */
export function initSentry(app, router) {

  //Only initialize Sentry if the environment is not local
  const {isLocal, env, gitHash} = useEnv()
  if (isLocal.value) {
    return
  }

  //Get user and environment refs
  const {user} = useCurrentUser()
  const {organization} = useCurrentOrganization()
  const {isInTeams, userId, tenantId, teamsAppId, teamsAppVersion} = useTeams()

  //DSN is safe to expose in the frontend
  //SEE: https://arist.sentry.io/projects/arist-messenger-vue/?project=4508656277061632
  const dsn = 'https://db3a18ec6b629f77028a1c9bf49c67fb@o454316.ingest.us.sentry.io/4508656277061632'

  //Set/update user once data becomes available
  watch([user, organization, userId, tenantId, teamsAppId, teamsAppVersion], () => {
    Sentry.setUser({
      ID: user.value?.id,
      Timezone: user.value?.timezone,
      Admin: user.value?.isAdmin,
      Organization: organization.value?.name,
      'Organization ID': organization.value?.id,
      'Teams User ID': userId.value,
      'Teams Tenant ID': tenantId.value,
      'Teams App ID': teamsAppId.value,
      'Teams App Version': teamsAppVersion.value,
    })
  })

  //Initialize Sentry
  //SEE: https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options
  Sentry.init({
    app,
    dsn,
    release: gitHash.value,
    environment: env.value,
    initialScope: scope => {
      scope.setTag('teams', isInTeams.value)
    },
    integrations: [
      Sentry.browserTracingIntegration({router}),
    ],
    trackComponents: [
      'App',
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      appUrls.DEV,
      appUrls.QA,
      appUrls.STAGING,
      appUrls.PRODUCTION,
    ],

    //Ignore errors thrown from bundled 3rd party libraries that we can't control
    denyUrls: [
      'node_modules/@floating-ui/vue',
      'node_modules/@microsoft/teams-js',
      'node_modules/@segment/analytics-next',
      'node_modules/@vueuse/core',
      'node_modules/@vue/runtime-dom',
      'node_modules/ky',
      'node_modules/luxon',
      'node_modules/marked',
      'node_modules/pinia',
      'node_modules/primevue',
      'node_modules/uuid',
    ],

    //Populate as needed with errors we want to ignore
    //NOTE: Every error added here *must* have a clear comment explaining why it is being ignored
    ignoreErrors: [

      //Specific network errors
      'Failed to fetch', //Generic fetch failures
      'Load failed', //iOS message for when fetch doesn't respond, likely a network issue
      'NetworkError when attempting to fetch resource', //CORS or network error
      'AbortError', //Occurs when a fetch request is aborted
      `Unexpected token '<'`, //Occurs when server might be down during deploy and returns a HTML page instead of JSON
      'Unexpected response format', //When server is awol, likely during a deploy
      'Unexpected end of JSON input', //When requests are cut short/incomplete responses
      'The string did not match the expected pattern', //Thrown by Safari when it cannot parse JSON return data from server
      'Error loading image', //Sometimes images just fail to load

      //Generic request/response errors
      /Request timed out/i, //Server request timeouts
      /Network error/i, //Generic network errors
      /Bad gateway/i, //Occurs when a request is made during a deploy
      /Internal server error/i, //Internal server errors (500)
      /Server error/i, //Generic server errors
      /Not found/i, //Generic 404 errors

      //Auth related
      'Unauthenticated', //Unauthenticated errors
      'Unauthorized', //Unauthorized errors
      'No organization', //Auth related
      'Organization not found for teams_tenant_id', //When backend can't find organization for this tenant
      'JWT::ExpiredSignature', //JWT expired
      'JWT::InvalidAudError', //JWT invalid audience

      //Non-errors, likely caused by requests that are made during deploys
      'Missing JSON data in response with model collection', //Guard in toModelCollection()
      'Missing JSON data in response with model instance', //Guard in toModelInstance()
      'Missing items key timezones in response JSON', //This endpoint works correctly, so probably a deploy issue

      //3rd party/browser internals errors
      'Large HTTP payload', //Sometimes we just have a large HTTP payload
      'Large Render Blocking Asset', //Browser complaining about slow loading assets
      'Object.hasOwn is not a function', //Thrown from within Ky if user is using an older browser
      'The user denied permission to access the database', //We don't use the browser database anyway
      'The requested file could not be read', //Permission problem that can occur after a reference to a file is acquired

      //Teams SDK errors
      'CancelledByUser', //Teams SDK get Teams auth token request was cancelled by user
      'Failed to get auth token due to user cancellation of consent flow', //Teams SDK get Teams auth token request was cancelled by user
      'Failed to get auth token. Token is empty due to auth provider library error.', //Teams SDK error
      'SDK initialization timed out', //Teams SDK sometimes barks
      'Error: IncorrectConfiguration', //Teams SDK sometimes barks
      'Error: NoNetwork', //When Teams SDK can't connect to the network to obtain a token
      'Auth Library Error', //Teams SDK sometimes barks
      'User canceled the flow', //User cancelled the auth flow
      'User cancelled the authorization session', //User cancelled the auth session
      'User declined to consent permission for this app', //🤷

      //Business logic errors
      'This enrollment is not available in Teams', //Disallowed enrollment view in Teams
      'Managing enrollments is not allowed for this organization', //Disallowed enrollment management
      'Unable to start or stop enrollment', //Sometimes enrollment can't be started or stopped but backend doesn't provide reason
      'Enrollment not found by code', //Enrollment not found by code

      //Noisy backend errors related mostly to data inconsistencies
      'address with microsoft_user_object_id', //When an address can't be found for a specific tenant
      'JWT::DecodeError Invalid audience', //Encountered when testing the app across various envs with mismatching audience configuration or when V3 backend wasn't deployed yet
      'user_id_mismatch', //Likely a data inconsistency
    ],
  })
}
